import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  List,
  message,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { FileExcelOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import { applyReportImport, previewReportImport } from "./reportDraftApi";
import type {
  ReportDraft,
  ReportImportField,
  ReportImportPreview,
  ReportImportRow,
} from "./reportDraftApi";
import { reportError, reportImportAccess } from "./reportDraftState";
import { ReportGuidanceDetails } from "./ReportRowGuidance";
import { guidanceDelta, guidanceMoneyText } from "./reportGuidanceState";
import {
  arrayBufferToBase64,
  createImportPreviewGuard,
  importActionLabels,
  importContextKey,
  importFileError,
  matchLabels,
} from "./reportImportState";

const { Text } = Typography;
const actionColor = (action: string) =>
  action === "Conflict"
    ? "red"
    : action === "Fill"
      ? "blue"
      : action === "Match"
        ? "green"
        : "default";
const cellValue = (value: string | null) =>
  value === null ? (
    <Text type="secondary">Дутуу</Text>
  ) : (
    <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {value}
    </Text>
  );

const fieldColumns = [
  { title: "Талбар", dataIndex: "label", key: "label", width: 180 },
  {
    title: "Excel-ийн утга",
    dataIndex: "excelValue",
    key: "excel",
    render: cellValue,
  },
  {
    title: "Системийн утга",
    dataIndex: "systemValue",
    key: "system",
    render: cellValue,
  },
  {
    title: "Нооргийн утга",
    dataIndex: "draftValue",
    key: "draft",
    render: cellValue,
  },
  {
    title: "Ноорог − Excel, ₮",
    key: "delta",
    width: 165,
    render: (_: unknown, field: ReportImportField) =>
      ["bankName", "accountNo"].includes(field.field) ? (
        <Text type="secondary">—</Text>
      ) : (
        guidanceMoneyText(
          guidanceDelta(field.draftValue, field.excelValue),
          true,
        )
      ),
  },
  {
    title: "Үр дүн",
    dataIndex: "action",
    key: "action",
    width: 190,
    render: (action: string) => (
      <Tag color={actionColor(action)}>
        {importActionLabels[action] ?? action}
      </Tag>
    ),
  },
];

type SelectedFile = { name: string; base64: string };
type Preview = { data: ReportImportPreview; token: number };
type Props = {
  report: ReportDraft;
  latest: ReportDraft | undefined;
  dirty: boolean;
  busy: boolean;
  canEdit: boolean;
  guidanceStale: boolean;
  onOpenRow: (requestId: string) => void;
  onApplied: (next: ReportDraft) => void;
  onBusyChange: (busy: boolean) => void;
  onRefresh: () => void;
};

export function RefundReportImport({
  report,
  latest,
  dirty,
  busy,
  canEdit,
  guidanceStale,
  onOpenRow,
  onApplied,
  onBusyChange,
  onRefresh,
}: Props) {
  const [file, setFile] = useState<SelectedFile | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState<"read" | "preview" | "apply" | null>(
    null,
  );
  const fileInput = useRef<HTMLInputElement>(null);
  const guard = useRef(createImportPreviewGuard());
  const fileSequence = useRef(0);
  const mounted = useRef(true);
  guard.current.setContext(importContextKey(report, latest, dirty));
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      guard.current.invalidate();
    };
  }, []);
  const validPreview =
    preview && guard.current.isCurrent(preview.token) ? preview.data : null;
  const savedImport = report.importInfo;
  const shownPreview = validPreview ?? savedImport;
  const isSaved = !validPreview && !!savedImport;
  const { editable, eligible, blockedReason } = reportImportAccess(
    report,
    latest,
    { canEdit, dirty },
  );

  function start(operation: "read" | "preview" | "apply") {
    setWorking(operation);
    onBusyChange(true);
    setError(null);
  }
  function finish() {
    if (mounted.current) {
      setWorking(null);
      onBusyChange(false);
    }
  }

  async function selectFile(event: ChangeEvent<HTMLInputElement>) {
    const selected = event.target.files?.[0];
    event.target.value = "";
    guard.current.invalidate();
    const sequence = ++fileSequence.current;
    setPreview(null);
    setFile(null);
    setError(null);
    if (!selected || !eligible) return;
    const invalid = importFileError(selected);
    if (invalid) {
      setError(invalid);
      return;
    }
    start("read");
    try {
      const base64 = arrayBufferToBase64(await selected.arrayBuffer());
      if (mounted.current && sequence === fileSequence.current)
        setFile({ name: selected.name, base64 });
    } catch {
      if (mounted.current && sequence === fileSequence.current)
        setError("Файлыг уншиж чадсангүй. .xlsx файлаа дахин сонгоно уу.");
    } finally {
      finish();
    }
  }

  async function reconcile() {
    if (!file || !eligible || busy || working) return;
    guard.current.invalidate();
    const token = guard.current.capture();
    setPreview(null);
    start("preview");
    try {
      const data = await previewReportImport({
        orderListId: report.orderListId,
        expectedVersion: report.version,
        sourceHash: report.sourceHash,
        fileName: file.name,
        fileBase64: file.base64,
      });
      if (mounted.current && guard.current.isCurrent(token))
        setPreview({ data, token });
    } catch (reason) {
      if (mounted.current && guard.current.isCurrent(token)) {
        setError(reportError(reason));
        onRefresh();
      }
    } finally {
      finish();
    }
  }

  async function apply() {
    if (!file || !validPreview?.canApply || !eligible || busy || working)
      return;
    const token = guard.current.capture();
    start("apply");
    try {
      const next = await applyReportImport({
        orderListId: report.orderListId,
        expectedVersion: report.version,
        sourceHash: report.sourceHash,
        fileName: file.name,
        fileBase64: file.base64,
        previewHash: validPreview.previewHash,
      });
      if (mounted.current) {
        guard.current.invalidate();
        setPreview(null);
        setFile(null);
        onApplied(next);
        message.success(
          "Тулгалтыг хадгалж, нөхөх боломжтой дутуу утгыг орууллаа. Үлдсэн зөрчлийг үргэлжлүүлэн хянана уу.",
        );
      }
    } catch (reason) {
      if (mounted.current && guard.current.isCurrent(token)) {
        setError(reportError(reason));
        guard.current.invalidate();
        onRefresh();
      }
    } finally {
      finish();
    }
  }

  const fillRows =
    shownPreview?.rows.flatMap((row) =>
      row.fields
        .filter((field) => field.action === "Fill")
        .map((field) => ({
          ...field,
          rowNumber: row.rowNumber,
          displayName: row.displayName,
          email: row.email,
        })),
    ) ?? [];
  const conflictRows =
    shownPreview?.rows.filter((row) =>
      row.fields.some((field) => field.action === "Conflict"),
    ) ?? [];
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Alert
        showIcon
        type="info"
        message="Нийт хуудасны тулгалт"
        description="2026-04-21-ний 8 хуудастай загварын бүтцийг шалгаж, «Нийт» хуудасны утгыг багцын хүсэлтүүдтэй тулгана. Мөн «Бонд», «Хугацаа дууссан бонд», «Хувьцаа» хуудасны хадгалсан цэвэр дүнгийн нийлбэрийг шалгана. Энэ нь бүх дэлгэрэнгүй тооцооны томьёог баталсан гэсэн үг биш."
      />
      <Alert
        showIcon
        type="warning"
        message="Эх файл ба томьёоны хадгалсан үр дүн"
        description="Эх Excel файлаа сонгоно уу. Томьёог дахин бодохгүй; файлд хамгийн сүүлд хадгалсан үр дүнг уншина. Хадгалсан үр дүн дутуу эсвэл хуучирсан бол эх файлын тооцоог нягталж, дахин хадгалсан хувилбарыг оруулна уу. Хоосон нүдийг 0 гэж үзэхгүй."
      />
      {editable && (
        <Card size="small" title="Хуучин Excel оруулах">
          <Space wrap>
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx"
              aria-label="Буцаалтын Excel файл"
              style={{ display: "none" }}
              onChange={selectFile}
              disabled={!eligible || busy || !!working}
            />
            <Button
              icon={<FileExcelOutlined />}
              disabled={!eligible || busy || !!working}
              onClick={() => fileInput.current?.click()}
              loading={working === "read"}
            >
              Excel сонгох
            </Button>
            <Text>{file?.name ?? ".xlsx · 2 MiB хүртэл"}</Text>
            <Button
              icon={<SearchOutlined />}
              disabled={!file || !eligible || busy || !!working}
              loading={working === "preview"}
              onClick={reconcile}
            >
              Нийт хуудсыг тулгах
            </Button>
          </Space>
          {blockedReason && (
            <p style={{ marginTop: 12, marginBottom: 0 }}>
              <Text type="warning">{blockedReason}</Text>
            </p>
          )}
          <p style={{ marginTop: 12, marginBottom: 0 }}>
            <Text type="secondary">
              Тулгалт хийхэд ноорогт утга хадгалахгүй. Үр дүнг хянасны дараа
              «Дутуу утгыг нөхөж хадгалах» үйлдлийг сонгоно.
            </Text>
          </p>
        </Card>
      )}
      {!editable && !savedImport && (
        <Alert type="info" showIcon message={blockedReason} />
      )}
      {error && <Alert type="error" showIcon message={error} />}
      {preview && !validPreview && (
        <Alert
          type="warning"
          showIcon
          message="Өгөгдөл эсвэл файл өөрчлөгдсөн тул өмнөх тулгалт хүчингүй болсон. Дахин тулгана уу."
        />
      )}
      {shownPreview && (
        <Card
          size="small"
          title={
            isSaved
              ? "Хадгалсан файлын одоогийн тулгалт"
              : "Импортын өмнөх тулгалт"
          }
        >
          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label="Эх файл">
              {shownPreview.fileName}
            </Descriptions.Item>
            <Descriptions.Item label="Excel-ийн мөр">
              {shownPreview.excelRowCount}
            </Descriptions.Item>
            <Descriptions.Item label="Багцын хүсэлт">
              {shownPreview.requestCount}
            </Descriptions.Item>
            <Descriptions.Item label="Таарсан мөр">
              {shownPreview.matchedCount}
            </Descriptions.Item>
            <Descriptions.Item label="Зөрүүтэй мөр">
              {conflictRows.length}
            </Descriptions.Item>
            <Descriptions.Item label="Нөхөх саналтай талбар">
              {fillRows.length}
            </Descriptions.Item>
            {isSaved && savedImport?.importedAt && (
              <Descriptions.Item label="Импорт хадгалсан">
                {moment(savedImport.importedAt).format("YYYY-MM-DD HH:mm:ss")}
              </Descriptions.Item>
            )}
          </Descriptions>
          {isSaved && (
            <p>
              <Text type="secondary">
                Хадгалсан эх файлыг тайлангийн энэ хувилбарын өгөгдөлтэй дахин
                тулгасан үр дүн. Дүн болон үлдсэн зөрчлийг «Хураангуй», «Мөрүүд»
                хэсгээс давхар хянана.
              </Text>
            </p>
          )}
          <List
            size="small"
            dataSource={shownPreview.issues}
            renderItem={(issue, index) => (
              <List.Item key={`${issue.code}-${index}`}>
                <Space>
                  <Tag color={issue.severity === "error" ? "red" : "orange"}>
                    {issue.severity === "error" ? "Зөрчил" : "Анхаарах"}
                  </Tag>
                  <span>{issue.message}</span>
                </Space>
              </List.Item>
            )}
          />
          {(conflictRows.length > 0 ||
            shownPreview.issues.some(
              (issue) => issue.severity === "error",
            )) && (
            <Alert
              style={{ margin: "12px 0" }}
              type="warning"
              showIcon
              message="Дутуу утгыг нөхөж болох ч тулгалтын зөрчил арилахгүй. Зөрчил үлдвэл хяналтад илгээх, тайлан батлах боломжгүй."
            />
          )}
          <Tabs
            items={[
              {
                key: "matches",
                label: "Мөрийн тулгалт",
                children: (
                  <Table<ReportImportRow>
                    size="small"
                    bordered
                    rowKey={(row) => String(row.rowNumber)}
                    dataSource={shownPreview.rows}
                    pagination={{ pageSize: 15 }}
                    scroll={{ x: 720 }}
                    expandable={{
                      expandedRowRender: (row) => (
                        <Space
                          direction="vertical"
                          size={12}
                          style={{ width: "100%" }}
                        >
                          <Table<ReportImportField>
                            size="small"
                            rowKey="field"
                            dataSource={row.fields}
                            columns={fieldColumns}
                            pagination={false}
                            scroll={{ x: 950 }}
                          />
                          {isSaved && row.requestId && (
                            <>
                              <ReportGuidanceDetails
                                items={
                                  report.rowGuidance?.find(
                                    (item) => item.requestId === row.requestId,
                                  )?.items ?? []
                                }
                                stale={guidanceStale}
                              />
                              <Button onClick={() => onOpenRow(row.requestId!)}>
                                Энэ хүний тайлбар, нөхөх талбар
                              </Button>
                            </>
                          )}
                        </Space>
                      ),
                    }}
                    columns={[
                      {
                        title: "Excel мөр",
                        dataIndex: "rowNumber",
                        width: 100,
                      },
                      {
                        title: "Харилцагч",
                        key: "name",
                        render: (_, row) => (
                          <Space direction="vertical" size={0}>
                            <Text>{row.displayName || "Нэргүй"}</Text>
                            <Text type="secondary">
                              {row.email ?? "Имэйл дутуу"}
                            </Text>
                          </Space>
                        ),
                      },
                      {
                        title: "Тулгалт",
                        key: "match",
                        render: (_, row) => (
                          <Tag
                            color={
                              row.matchStatus === "Matched" ? "green" : "red"
                            }
                          >
                            {matchLabels[row.matchStatus] ?? row.matchStatus}
                          </Tag>
                        ),
                      },
                      {
                        title: "Талбарын үр дүн",
                        key: "result",
                        render: (_, row) => (
                          <Space wrap>
                            {[
                              ...new Set(
                                row.fields.map((field) => field.action),
                              ),
                            ].map((action) => (
                              <Tag key={action} color={actionColor(action)}>
                                {importActionLabels[action] ?? action}:{" "}
                                {
                                  row.fields.filter(
                                    (field) => field.action === action,
                                  ).length
                                }
                              </Tag>
                            ))}
                          </Space>
                        ),
                      },
                    ]}
                  />
                ),
              },
              {
                key: "fills",
                label: `Нөхөх санал (${fillRows.length})`,
                children: (
                  <Table
                    size="small"
                    bordered
                    rowKey={(row) => `${row.rowNumber}-${row.field}`}
                    dataSource={fillRows}
                    pagination={{ pageSize: 15 }}
                    scroll={{ x: 900 }}
                    columns={[
                      {
                        title: "Excel мөр",
                        dataIndex: "rowNumber",
                        width: 100,
                      },
                      {
                        title: "Харилцагч",
                        dataIndex: "displayName",
                        width: 180,
                      },
                      ...fieldColumns,
                    ]}
                  />
                ),
              },
            ]}
          />
          {!isSaved && (
            <Space direction="vertical" style={{ marginTop: 16 }}>
              <Text>
                Зөвхөн «Дутуу утгыг нөхөх» талбарыг хадгална. Системийн
                баталгаажсан болон өмнө нөхсөн утгыг солихгүй.
              </Text>
              <Button
                type="primary"
                disabled={
                  !validPreview?.canApply || !eligible || busy || !!working
                }
                loading={working === "apply"}
                onClick={apply}
              >
                Дутуу утгыг нөхөж хадгалах
              </Button>
            </Space>
          )}
        </Card>
      )}
    </Space>
  );
}
